/**
 * CL-RE-HOME-002 / CL-RE-HOME-003 — Shared property workspace destinations (client + admin).
 * Canonical navigation IA; legacy ?tab= values map here without breaking deep links.
 */

import { normalizeCmaBuilderStep } from "./cmaBuilderSteps";
import { PROPERTY_RECORD_NAV_LABEL } from "./propertyRecordPresentationModel";
import {
  normalizeProfessionalCmaMode,
  PROFESSIONAL_CMA_MODES,
} from "./professionalCmaWorkspaceModel";

function safeStr(value) {
  return String(value ?? "").trim();
}

export const PROPERTY_WORKSPACE_DESTINATIONS = [
  { id: "overview", label: "Overview" },
  { id: "home_record", label: PROPERTY_RECORD_NAV_LABEL },
  { id: "products_systems", label: "Products & systems" },
  { id: "files_photos", label: "Files & photos" },
  { id: "plans_projects", label: "Projects" },
  { id: "market_sale", label: "Market & sale" },
];

const DESTINATION_IDS = new Set(PROPERTY_WORKSPACE_DESTINATIONS.map((d) => d.id));

/** Legacy ?tab= and alias values → canonical destination id. */
export const LEGACY_TAB_TO_DESTINATION = {
  overview: "overview",
  home_record: "home_record",
  home: "home_record",
  products: "products_systems",
  products_systems: "products_systems",
  files: "files_photos",
  files_photos: "files_photos",
  media: "files_photos",
  documents: "files_photos",
  plans: "plans_projects",
  plans_projects: "plans_projects",
  projects: "plans_projects",
  sale_prep: "market_sale",
  market: "market_sale",
  market_sale: "market_sale",
  market_selling: "market_sale",
  intelligence: "market_sale",
  insights: "market_sale",
  professional_cma: "market_sale",
  valuation: "market_sale",
  marks_valuation: "market_sale",
  cma_workbench: "market_sale",
  cma: "market_sale",
  workbench: "market_sale",
};

export const FILES_PHOTOS_VIEW = {
  ALL: "all",
  MEDIA: "media",
  DOCUMENTS: "documents",
};

const FILES_VIEW_SET = new Set(Object.values(FILES_PHOTOS_VIEW));

/** Top-level Market & sale sections (client + admin). */
export const MARKET_SALE_SECTION = {
  MARKET: "market",
  YOUR_SALE: "your_sale",
};

const MARKET_SECTION_SET = new Set(Object.values(MARKET_SALE_SECTION));

/** Admin / deep-link panes under Market (not Your sale). */
export const MARKET_SELLING_PANE = {
  HUB: "hub",
  INTELLIGENCE: "intelligence",
  PROFESSIONAL_CMA: "professional_cma",
};

const MARKET_PANE_SET = new Set(Object.values(MARKET_SELLING_PANE));

export function normalizePropertyWorkspaceDestination(tabValue) {
  const key = safeStr(tabValue).toLowerCase();
  const mapped = LEGACY_TAB_TO_DESTINATION[key];
  if (mapped && DESTINATION_IDS.has(mapped)) return mapped;
  return DESTINATION_IDS.has(key) ? key : "overview";
}

export function normalizeFilesPhotosView(value, { legacyTab = null } = {}) {
  const key = safeStr(value).toLowerCase();
  if (FILES_VIEW_SET.has(key)) return key;
  const legacy = safeStr(legacyTab).toLowerCase();
  if (legacy === "media") return FILES_PHOTOS_VIEW.MEDIA;
  if (legacy === "documents") return FILES_PHOTOS_VIEW.DOCUMENTS;
  return FILES_PHOTOS_VIEW.MEDIA;
}

export function normalizeMarketSaleSection(value, { legacyTab = null, designPreview = null } = {}) {
  const key = safeStr(value).toLowerCase();
  if (MARKET_SECTION_SET.has(key)) return key;
  if (safeStr(designPreview).toLowerCase() === "your_sale") return MARKET_SALE_SECTION.YOUR_SALE;
  const legacy = safeStr(legacyTab).toLowerCase();
  if (legacy === "sale_prep") return MARKET_SALE_SECTION.YOUR_SALE;
  return MARKET_SALE_SECTION.MARKET;
}

export function normalizeMarketSellingPane(value, { legacyTab = null, section = null, pcmaMode = null } = {}) {
  const key = safeStr(value).toLowerCase();
  if (MARKET_PANE_SET.has(key)) return key;
  const legacy = safeStr(legacyTab).toLowerCase();
  if (legacy === "professional_cma" || legacy === "valuation" || legacy === "marks_valuation" || legacy === "cma_workbench") {
    return MARKET_SELLING_PANE.PROFESSIONAL_CMA;
  }
  if (legacy === "intelligence" || legacy === "insights" || section) {
    return MARKET_SELLING_PANE.INTELLIGENCE;
  }
  if (pcmaMode) return MARKET_SELLING_PANE.PROFESSIONAL_CMA;
  return MARKET_SELLING_PANE.HUB;
}

/**
 * Resolve workspace destination + sub-views from URL search params.
 */
export function resolvePropertyWorkspaceRoute(searchParams) {
  const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
  const rawTab = safeStr(params.get("tab")).toLowerCase();
  const destination = normalizePropertyWorkspaceDestination(rawTab);
  const filesView = normalizeFilesPhotosView(params.get("filesView"), { legacyTab: rawTab });
  const section = safeStr(params.get("section")).toLowerCase() || null;
  const designPreview = params.get("designPreview");
  const marketSection = normalizeMarketSaleSection(params.get("marketSection"), {
    legacyTab: rawTab,
    designPreview,
  });
  const marketPane = normalizeMarketSellingPane(params.get("marketPane"), {
    legacyTab: rawTab,
    section,
    pcmaMode: params.get("pcmaMode"),
  });
  return { destination, filesView, marketSection, marketPane, section, rawTab, designPreview };
}

export function buildPropertyWorkspaceDestinationQuery(
  destination,
  {
    filesView = null,
    marketSection = null,
    marketPane = null,
    section = null,
    cmaStep = null,
    markValuationId = null,
    pcmaMode = null,
    requestId = null,
    adminMode = false,
  } = {}
) {
  const dest = normalizePropertyWorkspaceDestination(destination);
  const params = new URLSearchParams();
  if (dest !== "overview") params.set("tab", dest);

  if (dest === "files_photos" && filesView && filesView !== FILES_PHOTOS_VIEW.ALL) {
    params.set("filesView", normalizeFilesPhotosView(filesView));
  }

  if (dest === "market_sale") {
    const sectionNorm = normalizeMarketSaleSection(marketSection);
    if (sectionNorm !== MARKET_SALE_SECTION.MARKET) {
      params.set("marketSection", sectionNorm);
    }
    const pane = normalizeMarketSellingPane(marketPane);
    if (sectionNorm === MARKET_SALE_SECTION.MARKET && pane !== MARKET_SELLING_PANE.HUB) {
      params.set("marketPane", pane);
    }
    if (pane === MARKET_SELLING_PANE.INTELLIGENCE && section) {
      params.set("section", safeStr(section).toLowerCase());
    }
    if (pane === MARKET_SELLING_PANE.PROFESSIONAL_CMA) {
      let mode = pcmaMode ? normalizeProfessionalCmaMode(pcmaMode, { adminMode }) : null;
      if (!mode) {
        mode = adminMode ? PROFESSIONAL_CMA_MODES.BUILDER : PROFESSIONAL_CMA_MODES.PUBLISHED;
      }
      params.set("pcmaMode", mode);
      if (cmaStep) params.set("cmaStep", normalizeCmaBuilderStep(cmaStep));
      if (markValuationId) params.set("markValuationId", safeStr(markValuationId));
      if (requestId) params.set("requestId", safeStr(requestId));
    }
  }

  return params;
}

/**
 * Canonicalize legacy tab URLs to destination-based query (for Navigate replace).
 */
export function resolveLegacyDestinationRedirect(searchParams, { pathname = "" } = {}) {
  const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
  const rawTab = safeStr(params.get("tab")).toLowerCase();
  if (!rawTab && safeStr(params.get("designPreview")).toLowerCase() !== "your_sale") return null;

  const canonical = normalizePropertyWorkspaceDestination(rawTab || "market_sale");
  const legacyMapped = rawTab ? LEGACY_TAB_TO_DESTINATION[rawTab] : null;
  const needsTabCanonical = legacyMapped && rawTab !== canonical;

  const next = new URLSearchParams(params);
  let changed = false;

  if (needsTabCanonical) {
    next.set("tab", canonical);
    changed = true;
  }

  if (canonical === "files_photos" && (rawTab === "media" || rawTab === "documents")) {
    next.set("filesView", rawTab === "media" ? FILES_PHOTOS_VIEW.MEDIA : FILES_PHOTOS_VIEW.DOCUMENTS);
    changed = true;
  }

  if (canonical === "market_sale") {
    if (rawTab === "sale_prep") {
      next.set("marketSection", MARKET_SALE_SECTION.YOUR_SALE);
      changed = true;
    }
    if (
      rawTab === "intelligence" ||
      rawTab === "professional_cma" ||
      rawTab === "insights" ||
      rawTab === "valuation"
    ) {
      if (rawTab === "intelligence" || rawTab === "insights") {
        next.set("marketPane", MARKET_SELLING_PANE.INTELLIGENCE);
      } else {
        next.set("marketPane", MARKET_SELLING_PANE.PROFESSIONAL_CMA);
      }
      changed = true;
    }
  }

  if (safeStr(params.get("designPreview")).toLowerCase() === "your_sale") {
    next.set("tab", "market_sale");
    next.set("marketSection", MARKET_SALE_SECTION.YOUR_SALE);
    next.delete("designPreview");
    changed = true;
  }

  if (!changed) return null;
  const base = safeStr(pathname) || "";
  const query = next.toString();
  return query ? `${base}?${query}` : base;
}
